import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MessageSquare, RotateCcw, Send } from "lucide-react";
import { Btn, Section, inputCls } from "@/components/account/Shell";
import { Cell, Sheet, Tag, Tiles, Two, Txt } from "@/components/account/Sheet";
import api, { formatApiError } from "@/lib/api";

const clock = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const short = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

const COLS = [
    { key: "shop", label: "Shop", sub: "click the row to read the thread", w: "minmax(200px,1.3fr)" },
    { key: "owner", label: "Owner", sub: "login · space", w: "minmax(220px,1.3fr)" },
    { key: "messages", label: "Messages", w: "90px" },
    { key: "last", label: "Last message", w: "150px" },
    { key: "waiting", label: "Waiting on us", sub: "unread from the shop", w: "120px" },
    { key: "status", label: "Status", w: "100px" },
];

function Conversation({ chat, t, text, setText, busy, reply, setStatus }) {
    const endRef = useRef(null);
    useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [chat]);
    return (
        <Section testid="support-conversation" pad={false}
            title={<span data-testid="support-open-shop">{t.store_name}</span>} note={t.owner_name || t.owner_email}
            right={t.status === "closed" ? (
                <Btn onClick={() => setStatus("open")} testid="support-reopen"><RotateCcw size={11} /> reopen</Btn>
            ) : (
                <Btn onClick={() => setStatus("closed")} testid="support-close"><CheckCircle2 size={11} /> sorted</Btn>
            )}>
            <div className="max-h-[22rem] space-y-2 overflow-y-auto bg-[#f5f5f5] p-2.5" data-testid="support-messages">
                {(chat.messages || []).length === 0 && <p className="text-sm text-[#4a4a4a]">Nothing said yet.</p>}
                {(chat.messages || []).map((m) => (
                    <div key={m.id} data-testid={`support-msg-${m.from}`}
                        className={`max-w-[80%] border px-2.5 py-1.5 text-sm leading-snug ${m.from === "office"
                            ? "ml-auto border-[#1a1a1a] bg-[#1a1a1a] text-white bo-inv" : "mr-auto border-[#d0d0d0] bg-white text-[#1a1a1a]"}`}>
                        {m.body}
                        <span className={`mt-0.5 block text-[11px] ${m.from === "office" ? "text-white/60" : "text-[#4a4a4a]"}`}>
                            {m.from === "office" ? "13thav support" : t.store_name} · {short(m.created_at)}
                        </span>
                    </div>
                ))}
                <div ref={endRef} />
            </div>
            <form onSubmit={reply} className="flex items-center gap-1.5 border-t border-[#d0d0d0] p-2">
                <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Answer the shop…"
                    data-testid="support-reply-input" className={inputCls} />
                <Btn type="submit" tone="solid" disabled={busy || !text.trim()} testid="support-reply-send">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send
                </Btn>
            </form>
            <p className="px-2.5 pb-2 text-[11px] text-[#4a4a4a]">Every answer is also emailed to the shop, so nothing sits unread.</p>
        </Section>
    );
}

/** Platform admin → Live chat: every shop's thread, answered from behind the counter. */
export default function SupportAdmin() {
    const [d, setD] = useState(null);
    const [openId, setOpenId] = useState("");
    const [chat, setChat] = useState(null);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);

    const list = useCallback(() => api.get("/platform/support")
        .then(({ data }) => setD(data)).catch(() => setD({ threads: [], counts: {} })), []);
    const open = useCallback((id) => api.get(`/platform/support/${id}`)
        .then(({ data }) => setChat(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);

    useEffect(() => { list(); const t = setInterval(list, 20000); return () => clearInterval(t); }, [list]);
    useEffect(() => {
        if (!openId) { setChat(null); return; }
        open(openId);
        const t = setInterval(() => open(openId), 10000);
        return () => clearInterval(t);
    }, [openId, open]);

    const reply = async (e) => {
        e.preventDefault();
        const body = text.trim();
        if (!body) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/platform/support/${openId}`, { body });
            setChat((prev) => ({ ...(prev || {}), messages: data.messages, thread: data.thread }));
            setText("");
            toast.success(data.note);
            list();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const setStatus = async (status) => {
        try {
            const { data } = await api.post(`/platform/support/${openId}/status`, { status });
            toast.success(data.note);
            setChat((prev) => ({ ...(prev || {}), thread: { ...(prev?.thread || {}), status } }));
            list();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    if (!d) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#4a4a4a]" /></div>;
    const threads = d.threads || [];
    const t = chat?.thread || null;

    return (
        <div className="space-y-3" data-testid="support-admin">
            <Tiles items={[
                ["Shops on the line", threads.length, "threads open with the office"],
                ["Waiting on us", <span data-testid="support-waiting">{d.counts?.unread || 0}</span>, "messages we haven't answered"],
                ["The line", <span data-testid="support-line-state" className={d.line?.open ? "text-[#067647]" : "text-[#b54708]"}>{d.line?.open ? "Open" : "Away"}</span>, d.line?.hours_label],
                ["Open thread", t ? t.store_name : "—", t ? "answer it below" : "click a row to open one"],
            ]} />

            <Section title={<span className="inline-flex items-center gap-1.5"><MessageSquare size={13} /> {threads.length} thread{threads.length === 1 ? "" : "s"}</span>}
                pad={false} testid="support-threads" note="click a shop to read the thread and answer it right under the row">
                <Sheet cols={COLS} rows={threads} rowKey={(x) => x.id} testid="support-sheet" minWidth={900}
                    rowTestid={(x) => `support-thread-${x.store_slug}`} onRowClick={(x) => setOpenId(openId === x.id ? "" : x.id)}
                    active={(x) => x.id === openId} empty="No shop has opened the chat line yet."
                    expand={() => (openId && chat && t
                        ? <Conversation chat={chat} t={t} text={text} setText={setText} busy={busy} reply={reply} setStatus={setStatus} />
                        : <div className="flex justify-center py-6"><Loader2 className="animate-spin text-[#4a4a4a]" /></div>)}
                    cells={(x) => ({
                        shop: <Cell><Txt bold>{x.store_name}</Txt></Cell>,
                        owner: <Cell><Two a={x.owner_email} b={x.tier} /></Cell>,
                        messages: <Cell><Txt mono right>{x.messages}</Txt></Cell>,
                        last: <Cell><Txt sub>{x.last_message_at ? clock(x.last_message_at) : "—"}</Txt></Cell>,
                        waiting: (
                            <Cell>
                                {x.unread_office
                                    ? <Tag status="booked" testid={`support-unread-${x.store_slug}`}>{x.unread_office} new</Tag>
                                    : <Txt sub>—</Txt>}
                            </Cell>
                        ),
                        status: <Cell><Tag status={x.status === "closed" ? "closed" : "open"} /></Cell>,
                    })} />
                {!threads.length && <p className="sr-only" data-testid="support-none">No shop has opened the chat line yet.</p>}
                {!openId && !!threads.length && (
                    <p className="border-t border-[#d0d0d0] px-2.5 py-2 text-[12px] text-[#4a4a4a]" data-testid="support-pick">
                        Pick a shop above to read the thread and answer it.
                    </p>
                )}
            </Section>
        </div>
    );
}
